/**
 * SWR Keys registry
 * Centralized place for all SWR cache keys to ensure consistency
 */

export const swrKeys = {
  auth: {
    user: () => '/app/v1/users/me' as const,
  },
  projects: {
    all: () => '/app/v1/projects' as const,
    byId: (id: string) => `/app/v1/projects/${id}` as const,
  },
  invoices: {
    all: (params?: Record<string, unknown>) => ['/app/v1/invoices', params] as const,
    byId: (id: string) => `/api/v1/invoices/${id}` as const,
    stats: () => '/app/v1/invoices/statistic' as const,
  },
  wallets: {
    all: (params?: Record<string, unknown>) => ['/app/v1/wallets', params] as const,
    groups: () => '/app/v1/wallets/groups' as const,
    addresses: (walletId: number | string, params?: Record<string, unknown>) =>
      [`/app/v1/wallets/${walletId}/addresses`, params] as const,
    byId: (id: string) => `/app/v1/wallets/${id}` as const,
  },
  withdrawals: {
    all: (params?: Record<string, unknown>) => ['/app/v1/withdrawals', params] as const,
    admin: (params?: Record<string, unknown>) => ['/app/v1/withdrawals/admin', params] as const,
    emergencyGrants: (withdrawalId: string) => ['/app/v1/admin/withdrawal-emergencies', withdrawalId] as const,
    limits: () => '/app/v1/withdrawals/limits' as const,
    policy: () => '/app/v1/admin/withdrawal-policy' as const,
  },
  accounting: {
    balance: () => '/app/v1/balances' as const,
    all: (params?: Record<string, unknown>) => ['/app/v1/balances/accountings', params] as const,
    service: (params?: Record<string, unknown>) => ['/app/v1/balances/accountings/service', params] as const,
    partners: (params?: Record<string, unknown>) => ['/app/v1/balances/accountings/partners', params] as const,
  },
  referral: {
    partnersBalance: () => '/app/v1/balances/partners' as const,
    partnersAccountings: (params?: Record<string, unknown>) => ['/app/v1/balances/accountings/partners', params] as const,
    partnersCount: () => '/app/v1/meta/partners-count' as const,
  },
  addressAml: {
    all: (params?: Record<string, unknown>) => ['/app/v1/address-aml', params] as const,
    addresses: (params?: Record<string, unknown>) => ['/app/v1/address', params] as const,
  },
  meta: {
    currencies: () => '/app/v1/meta/currencies' as const,
  },
  ipWhitelist: {
    all: (params?: Record<string, unknown>) => ['/app/v1/ip-whitelists', params] as const,
    byId: (id: string | number) => `/app/v1/ip-whitelists/${id}` as const,
  },
  auditLog: {
    list: (search: string) => ['/app/v1/audit-logs', search] as const,
    byId: (id: string) => `/app/v1/audit-logs/${encodeURIComponent(id)}` as const,
  },
  security: {
    settings: () => '/app/v1/security/settings' as const,
    sessions: () => '/app/v1/security/sessions' as const,
    loginHistory: (before = '') => ['/app/v1/security/login-history', before] as const,
    trustedDevices: () => '/app/v1/security/trusted-devices' as const,
    ipWhitelist: () => '/app/v1/security/ip-whitelist' as const,
    withdrawalAddresses: () => '/app/v1/security/withdrawal-addresses' as const,
  },
  compliance: {
    queue: (params?: Record<string, unknown>) => ['/app/v1/admin/compliance/withdrawals', params] as const,
    card: (id: string | number) => `/app/v1/admin/compliance/withdrawals/${id}` as const,
    decisions: () => '/app/v1/admin/compliance/decisions' as const,
    addresses: () => '/app/v1/admin/compliance/addresses' as const,
    reviewers: () => '/app/v1/admin/compliance/reviewers' as const,
    messages: (id: string | number) => `/app/v1/withdrawals/${id}/compliance-messages` as const,
  },
  aml: {
    policy: () => '/app/v1/admin/aml-risk-policy' as const,
    billingSettings: () => '/app/v1/admin/aml-billing/settings' as const,
    merchantBilling: (userId: string | number) => `/app/v1/admin/aml-billing/merchants/${userId}` as const,
    chargesSummary: (month: string) => ['/app/v1/admin/aml-charges/summary', month] as const,
    adminCharges: (month: string, merchantId = '') => ['/app/v1/admin/aml-charges', month, merchantId] as const,
    myCharges: (month: string) => ['/app/v1/aml-charges', month] as const,
  },
  reconciliation: {
    runs: (params?: Record<string, unknown>) => ['/app/v1/admin/reconciliation/runs', params] as const,
    run: (id: string) => `/app/v1/admin/reconciliation/runs/${id}` as const,
    runRows: (id: string, params?: Record<string, unknown>) =>
      [`/app/v1/admin/reconciliation/runs/${id}/rows`, params] as const,
    discrepancies: (params?: Record<string, unknown>) =>
      ['/app/v1/admin/reconciliation/discrepancies', params] as const,
    discrepancy: (id: string) => `/app/v1/admin/reconciliation/discrepancies/${id}` as const,
    suspense: (params?: Record<string, unknown>) => ['/app/v1/admin/reconciliation/suspense', params] as const,
    suspenseItem: (id: string) => `/app/v1/admin/reconciliation/suspense/${id}` as const,
    networkCosts: (params?: Record<string, unknown>) => ['/app/v1/admin/network-costs', params] as const,
    policies: () => '/app/v1/admin/network-cost-policies' as const,
  },
};
